import { transport } from '@opengov/app-core/transport';
import {
  getSubjectPublicationClaimByPublicationIdSubjectId,
  getSubjectPublicationClaims,
  groupsByMemberId
} from '@opengov/stories-shared/graphQL';
import { sanitizeSectionIdsForClaims } from '@opengov/stories-shared/utils';
import * as qs from 'query-string';

import {
  createSubjectPublicationClaims,
  createSubjectStoryClaims,
  deleteSubjectPublicationClaim,
  deleteSubjectStoryClaim,
  getSubjectStoryClaimsByStoryId,
  getSubjectStoryClaimsByStoryIdsSubjectIds
} from '../graphQL/queries/subjectClaimsQueries';
import {
  IExtendedStoryPrivilege,
  ISubjectClaims,
  ISubjectPublicationClaim,
  ISubjectPublicationClaimsList,
  ISubjectStoryClaim,
  ISubjectStoryClaimsList
} from '../../types';

interface GraphQLError {
  message: string;
}

interface GraphQLResponse<T> {
  data?: T;
  errors?: GraphQLError[];
}

async function postStoriesGraphQL<T>(body: {
  query: unknown;
  variables?: Record<string, unknown>;
}): Promise<T> {
  const response = await transport
    .url(`/api/data/objects/stories/v1/graphql`)
    .json(body)
    .post()
    .json<GraphQLResponse<T>>();

  if (response.errors?.length) {
    throw new Error(response.errors[0]?.message ?? 'GraphQL request failed');
  }

  if (!response.data) {
    throw new Error('GraphQL request returned no data');
  }

  return response.data;
}

interface GroupsByMemberIdResponse {
  data: {
    groupsByMemberId: {
      groups: { id: string; name: string }[];
    };
  };
}

export interface FetchCurrentSubjectClaimsParams {
  userUuid: string;
  entityUuid: string;
}

export async function fetchCurrentSubjectClaims({
  userUuid,
  entityUuid
}: FetchCurrentSubjectClaimsParams): Promise<ISubjectClaims> {
  const variables = {
    subject: `urn:opengov:platform:user/${userUuid}`,
    context: `urn:opengov:platform:entity/${entityUuid}`,
    claim: 'STORIES_*'
  };
  const query = qs.stringify(variables, { encode: false });
  return transport.url(`/api/access/policy/v1/claims/verify?${query}`).get().json<ISubjectClaims>();
}

export interface FetchSubjectPublicationStoryClaimParams {
  publicationId?: string;
  subjectId: string;
  storyId?: string;
  entityUuid: string;
  stories_access_control_enabled: boolean;
}

export interface SubjectPublicationStoryClaimResult {
  allClaims: IExtendedStoryPrivilege;
  sectionIds?: string[] | 'All';
}

export async function fetchSubjectPublicationStoryClaim({
  publicationId,
  subjectId,
  storyId,
  entityUuid,
  stories_access_control_enabled
}: FetchSubjectPublicationStoryClaimParams): Promise<SubjectPublicationStoryClaimResult | undefined> {
  const response = await transport
    .url(`/api/stories/v1/publications/${publicationId}/claims`)
    .json({
      story_id: storyId ?? null,
      subject_id: subjectId,
      entity_uuid: entityUuid,
      stories_access_control_enabled
    })
    .post()
    .json<{ claims: SubjectPublicationStoryClaimResult }>();

  return response.claims;
}

export async function fetchStoryClaims(storyId: string): Promise<ISubjectStoryClaimsList> {
  const data = await postStoriesGraphQL<{ subjectStoryClaims: ISubjectStoryClaimsList }>({
    query: getSubjectStoryClaimsByStoryId,
    variables: { storyId }
  });
  return data.subjectStoryClaims ?? [];
}

export async function fetchStoryClaimsBySubjects(
  storyIds: string[],
  subjectIds: string[]
): Promise<ISubjectStoryClaimsList> {
  if (storyIds.length === 0 || subjectIds.length === 0) {
    return [];
  }

  const data = await postStoriesGraphQL<{ subjectStoryClaims: ISubjectStoryClaimsList }>({
    query: getSubjectStoryClaimsByStoryIdsSubjectIds,
    variables: { storyIds, subjectIds }
  });
  return data.subjectStoryClaims ?? [];
}

export interface FetchPublicationClaimsParams {
  publicationId: string;
  publicationClaims?: string[];
}

export async function fetchPublicationClaims({
  publicationId,
  publicationClaims
}: FetchPublicationClaimsParams): Promise<ISubjectPublicationClaimsList> {
  const where = publicationClaims
    ? {
        publicationId: { _eq: publicationId },
        publicationClaim: { _in: publicationClaims }
      }
    : {
        publicationId: { _eq: publicationId }
      };
  const data = await postStoriesGraphQL<{ subjectPublicationClaims: ISubjectPublicationClaimsList }>({
    query: getSubjectPublicationClaims,
    variables: { where }
  });
  return data.subjectPublicationClaims ?? [];
}

export async function fetchPublicationClaimBySubjects(
  subjectIds: string[],
  publicationId: string
): Promise<ISubjectPublicationClaimsList> {
  if (!publicationId || subjectIds.length === 0) {
    return [];
  }

  const data = await postStoriesGraphQL<{ subjectPublicationClaims: ISubjectPublicationClaimsList }>({
    query: getSubjectPublicationClaimByPublicationIdSubjectId,
    variables: { subjectIds, publicationId }
  });
  return data.subjectPublicationClaims ?? [];
}

export async function saveStoryClaims(subjectStoryClaimsList: ISubjectStoryClaimsList): Promise<void> {
  if (subjectStoryClaimsList.length === 0) {
    return;
  }

  await postStoriesGraphQL({
    query: createSubjectStoryClaims,
    variables: { subjectStoryClaimsList }
  });
}

export async function saveSubjectPublicationClaims(
  subjectPublicationClaimsList: ISubjectPublicationClaimsList,
  publicationId: string
): Promise<{ newSubjects: ISubjectPublicationClaimsList }> {
  if (!publicationId || subjectPublicationClaimsList.length === 0) {
    return { newSubjects: [] };
  }

  const sanitizedList = subjectPublicationClaimsList.map((claim: ISubjectPublicationClaim) => ({
    ...claim,
    sectionIds: sanitizeSectionIdsForClaims(claim.sectionIds || [])
  }));

  const existingClaims = await fetchPublicationClaimBySubjects(
    sanitizedList.map((claim) => claim.subjectId),
    publicationId
  );
  const existingSubjectIds = new Set(existingClaims.map((claim) => claim.subjectId));
  const newSubjects = sanitizedList.filter((claim) => !existingSubjectIds.has(claim.subjectId));

  await postStoriesGraphQL({
    query: createSubjectPublicationClaims,
    variables: { subjectPublicationClaimsList: sanitizedList }
  });

  if (newSubjects.length > 0) {
    void transport
      .url(`/api/stories/v1/publications/${publicationId}/invite-collaborators`)
      .json({
        subjects: newSubjects,
        publicationId
      })
      .post();
  }

  return { newSubjects };
}

export async function deleteSubjectStoryClaims(subjectIds: string[], storyId: string): Promise<void> {
  if (!storyId || subjectIds.length === 0) {
    return;
  }

  await postStoriesGraphQL({
    query: deleteSubjectStoryClaim,
    variables: { subjectIds, storyId }
  });
}

export interface DeleteSubjectPublicationClaimsParams {
  subjectIds: string[];
  publicationId: string;
  entityId: number;
  entityUuid: string;
}

export async function deleteSubjectPublicationClaims({
  subjectIds,
  publicationId,
  entityId,
  entityUuid
}: DeleteSubjectPublicationClaimsParams): Promise<void> {
  if (!publicationId || subjectIds.length === 0) {
    return;
  }

  await postStoriesGraphQL({
    query: deleteSubjectPublicationClaim,
    variables: { subjectIds, publicationId }
  });

  await transport
    .url(`/api/stories/v1/publications/${publicationId}/deleteStoryClaims`)
    .json({
      subject_ids: subjectIds,
      entity_id: entityId,
      entity_uuid: entityUuid
    })
    .post();
}

export async function getCurrentUserGroupsIds(subjectId: string, entityUuid: string): Promise<string[]> {
  const response = await transport
    .url(`/api/access/group/v1/graphql`)
    .json({
      query: groupsByMemberId,
      variables: {
        memberId: subjectId,
        entityId: entityUuid
      }
    })
    .post()
    .json<GroupsByMemberIdResponse>();
  return response?.data?.groupsByMemberId?.groups?.map((group) => group.id) ?? [];
}

interface SyncPublicationClaimsParams {
  storyClaimsList: ISubjectStoryClaimsList;
  publicationId: string;
  sectionIds: string[];
  manageShared: string;
}

export async function syncPublicationClaims({
  storyClaimsList,
  publicationId,
  sectionIds,
  manageShared
}: SyncPublicationClaimsParams): Promise<void> {
  const storySubjectIds = [...new Set(storyClaimsList.map((claim) => claim.subjectId))];
  const existingClaims = await fetchPublicationClaimBySubjects(storySubjectIds, publicationId);
  const existingSubjectIds = new Set(existingClaims.map((claim) => claim.subjectId));
  const newSubjectIds = storySubjectIds.filter((id) => !existingSubjectIds.has(id));

  const updatedExistingClaims = existingClaims.map((claim) => {
    const existingSectionIds = claim.sectionIds || [];
    const mergedSectionIds = [...new Set([...existingSectionIds, ...sectionIds])];
    return {
      ...claim,
      sectionIds: sanitizeSectionIdsForClaims(mergedSectionIds)
    };
  });

  if (updatedExistingClaims.length > 0) {
    await saveSubjectPublicationClaims(updatedExistingClaims, publicationId);
  }

  if (newSubjectIds.length > 0) {
    const subjectInfo = storyClaimsList.map((claim) => ({
      subjectId: claim.subjectId,
      subjectType: claim.subjectType
    }));
    const newPublicationClaims: ISubjectPublicationClaimsList = newSubjectIds.map((id) => ({
      subjectId: id,
      subjectType: subjectInfo.find((info) => info.subjectId === id)?.subjectType || '',
      publicationId,
      publicationClaim: manageShared,
      sectionIds: sanitizeSectionIdsForClaims(sectionIds)
    })) as ISubjectPublicationClaimsList;

    await saveSubjectPublicationClaims(newPublicationClaims, publicationId);
  }
}

export interface SaveSubjectStoryClaimParams {
  subjectStoryClaimsList: ISubjectStoryClaimsList;
  publicationId: string;
  sectionIds: string[];
  manageShared: string;
  // Single-story access: notify subjects newly given access to the story.
  notifyNewCollaborators?: boolean;
  // Bulk story access: notify every saved subject once, linking to the publication.
  notifyBulkCollaborators?: boolean;
}

const hasStoryClaims = (claim: ISubjectStoryClaim): boolean => claim.storyClaims?.length > 0;

function notifyNewStoryCollaborators(publicationId: string, subjects: ISubjectStoryClaim[]): void {
  if (!publicationId || subjects.length === 0) {
    return;
  }

  void transport
    .url(`/api/stories/v1/publications/${publicationId}/stories/invite-collaborators`)
    .json({
      subjects: subjects.map(({ subjectId, subjectType, storyId }) => ({ subjectId, subjectType, storyId }))
    })
    .post()
    .res()
    .catch(() => undefined);
}

function notifyBulkStoryCollaborators(publicationId: string, subjectStoryClaims: ISubjectStoryClaim[]): void {
  const grantedClaims = subjectStoryClaims.filter(hasStoryClaims);
  if (!publicationId || grantedClaims.length === 0) {
    return;
  }

  // Bulk access pairs every subject with every selected story, so the ids are sent once each
  // instead of as subject/story pairs.
  const subjects = new Map(
    grantedClaims.map(({ subjectId, subjectType }) => [subjectId, { subjectId, subjectType }])
  );
  void transport
    .url(`/api/stories/v1/publications/${publicationId}/stories/bulk-invite-collaborators`)
    .json({
      storyIds: [...new Set(grantedClaims.map((claim) => claim.storyId))],
      subjects: [...subjects.values()]
    })
    .post()
    .res()
    .catch(() => undefined);
}

const storyClaimKey = (subjectId: string, storyId: string): string => `${subjectId.toLowerCase()}::${storyId}`;

// Best-effort: a failed lookup must never block the claim save, it only skips the notification.
async function findNewStoryGrants(
  subjectStoryClaimsList: ISubjectStoryClaim[],
  publicationId: string
): Promise<ISubjectStoryClaim[]> {
  const candidates = subjectStoryClaimsList.filter(hasStoryClaims);
  if (!publicationId || candidates.length === 0) {
    return [];
  }

  try {
    const storyIds = [...new Set(candidates.map((claim) => claim.storyId))];
    const subjectIds = [...new Set(candidates.map((claim) => claim.subjectId))];
    const existingClaims: ISubjectStoryClaim[] = await fetchStoryClaimsBySubjects(storyIds, subjectIds);
    // A row whose privileges were all unchecked is kept with empty storyClaims; it is not access.
    const existingKeys = new Set(
      existingClaims.filter(hasStoryClaims).map((claim) => storyClaimKey(claim.subjectId, claim.storyId))
    );

    return candidates.filter((claim) => !existingKeys.has(storyClaimKey(claim.subjectId, claim.storyId)));
  } catch {
    return [];
  }
}

export async function saveSubjectStoryClaimWithSync({
  subjectStoryClaimsList,
  publicationId,
  sectionIds,
  manageShared,
  notifyNewCollaborators = false,
  notifyBulkCollaborators = false
}: SaveSubjectStoryClaimParams): Promise<void> {
  const newGrants = notifyNewCollaborators
    ? await findNewStoryGrants(subjectStoryClaimsList, publicationId)
    : [];
  await saveStoryClaims(subjectStoryClaimsList);
  notifyNewStoryCollaborators(publicationId, newGrants);
  if (notifyBulkCollaborators) {
    notifyBulkStoryCollaborators(publicationId, subjectStoryClaimsList);
  }
  await syncPublicationClaims({
    storyClaimsList: subjectStoryClaimsList,
    publicationId,
    sectionIds,
    manageShared
  });
}
